
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { toast } from "sonner";
import { UserType } from "@/types/user";
import { differenceInDays } from "date-fns";

// Define plan limits in a single place
export const PLAN_LIMITS = {
  free: 2,
  starter: 10,
  pro: 30,
  premium: 60,
  admin: Infinity,
};

export type SubscriptionPlan = keyof typeof PLAN_LIMITS;

export const useSubscriptionManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast: uiToast } = useToast();

  /**
   * Get the current user's subscription details
   */
  const getUserSubscription = async (userId: string) => {
    try {
      // Get user profile data
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("user_type, used_free_report, stripe_customer_id")
        .eq("id", userId)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        throw new Error("Failed to fetch user profile");
      }

      // Normalize user type - treat 'standard' as 'free'
      let userType = profileData?.user_type as UserType;
      if (profileData?.user_type === 'standard') {
        userType = 'free';
      }

      // Get report usage data
      const { data: reportUsage, error: reportUsageError } = await supabase
        .from("report_usage")
        .select("reports_generated, last_report_date, last_reset_at")
        .eq("user_id", userId)
        .single();

      if (reportUsageError && reportUsageError.code !== "PGRST116") {
        console.error("Error fetching report usage:", reportUsageError);
      }

      // Check if we need to reset reports based on last_reset_at
      let reportsGenerated = reportUsage?.reports_generated || 0;
      if (reportUsage?.last_reset_at) {
        const lastResetDate = new Date(reportUsage.last_reset_at);
        const today = new Date();
        const daysSinceReset = differenceInDays(today, lastResetDate);
        
        if (daysSinceReset >= 30) {
          // It's been 30+ days, so we should reset the count
          const { error: resetError } = await supabase
            .from("report_usage")
            .update({ 
              reports_generated: 0, 
              last_reset_at: today.toISOString() 
            })
            .eq("user_id", userId);
            
          if (resetError) {
            console.error("Error resetting report count:", resetError);
          } else {
            console.log("Reset report count after 30 days for user:", userId);
            reportsGenerated = 0;
          }
        }
      }

      return {
        userType,
        usedFreeReport: profileData?.used_free_report || false,
        stripeCustomerId: profileData?.stripe_customer_id,
        reportsGenerated,
        lastReportDate: reportUsage?.last_report_date || null,
        lastResetDate: reportUsage?.last_reset_at || null,
      };
    } catch (error) {
      console.error("Error getting user subscription:", error);
      throw error;
    }
  };

  /**
   * Check if a user has reached their report limit
   */
  const checkReportLimit = async (userId: string) => {
    try {
      const subscription = await getUserSubscription(userId);

      // Admin users have no limits
      if (subscription.userType === "admin") {
        return { 
          hasReachedLimit: false, 
          remainingReports: Infinity,
          isFreeReport: false,
          isPaidReport: false
        };
      }

      // Get the plan limit
      const planLimit =
        PLAN_LIMITS[subscription.userType as SubscriptionPlan] || PLAN_LIMITS.free;

      // Calculate remaining reports
      const remainingReports = planLimit - subscription.reportsGenerated;
      
      // Determine if this is a free report
      const isFreeReport = !subscription.usedFreeReport && 
        subscription.userType === 'free';
      
      // Check if it's a paid plan
      const isPaidReport = (
        subscription.userType === 'starter' || 
        subscription.userType === 'pro' || 
        subscription.userType === 'premium'
      );
      
      return {
        hasReachedLimit: remainingReports <= 0,
        remainingReports: Math.max(0, remainingReports),
        isFreeReport,
        isPaidReport
      };
    } catch (error) {
      console.error("Error checking report limit:", error);
      throw error;
    }
  };

  /**
   * Mark a free report as used
   */
  const markFreeReportUsed = async (userId: string) => {
    try {
      console.log("Marking free report as used for user:", userId);

      // Update the user's profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ used_free_report: true })
        .eq("id", userId);

      if (profileError) {
        console.error("Error marking free report as used:", profileError);
        throw profileError;
      }

      // Create or update report usage record
      await updateReportUsage(userId);

      return true;
    } catch (error) {
      console.error("Error marking free report as used:", error);
      return false;
    }
  };

  /**
   * Increment the report usage counter
   */
  const updateReportUsage = async (userId: string) => {
    try {
      // Check if we need to reset reports based on last_reset_at
      const { data: reportUsage, error: checkError } = await supabase
        .from("report_usage")
        .select("reports_generated, last_reset_at")
        .eq("user_id", userId)
        .single();

      let reportsGenerated = 0;
      let shouldReset = false;

      if (checkError) {
        if (checkError.code !== "PGRST116") {
          console.error("Error checking existing report usage:", checkError);
          return false;
        }
      } else if (reportUsage) {
        // Check if it's been 30+ days since the last reset
        if (reportUsage.last_reset_at) {
          const lastResetDate = new Date(reportUsage.last_reset_at);
          const today = new Date();
          const daysSinceReset = differenceInDays(today, lastResetDate);
          
          if (daysSinceReset >= 30) {
            console.log(`It's been ${daysSinceReset} days since the last reset. Resetting report count.`);
            shouldReset = true;
            reportsGenerated = 1; // Starting fresh with this new report
          } else {
            reportsGenerated = reportUsage.reports_generated + 1;
          }
        } else {
          reportsGenerated = reportUsage.reports_generated + 1;
        }
      }

      if (reportUsage) {
        // Update existing record
        const { error: updateError } = await supabase
          .from("report_usage")
          .update({
            reports_generated: reportsGenerated,
            last_report_date: new Date().toISOString(),
            ...(shouldReset ? { last_reset_at: new Date().toISOString() } : {})
          })
          .eq("user_id", userId);

        if (updateError) {
          console.error("Error updating report usage:", updateError);
          return false;
        }
      } else {
        // Create new record
        const { error: insertError } = await supabase
          .from("report_usage")
          .insert({
            user_id: userId,
            reports_generated: 1,
            last_report_date: new Date().toISOString(),
            last_reset_at: new Date().toISOString()
          });

        if (insertError) {
          console.error("Error creating report usage:", insertError);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error updating report usage:", error);
      return false;
    }
  };

  /**
   * Handle subscription upgrade logic
   * When upgrading from starter to pro, users get 30 more reports plus their remaining reports
   */
  const handleSubscriptionUpgrade = async (
    userId: string,
    newPlanType: UserType,
    previousPlanType?: UserType
  ) => {
    setIsLoading(true);

    try {
      // If we don't know the previous plan, fetch it
      if (!previousPlanType) {
        const { data, error } = await supabase
          .from("profiles")
          .select("user_type")
          .eq("id", userId)
          .single();

        if (error) {
          console.error("Error fetching previous plan type:", error);
          throw new Error("Failed to fetch previous plan type");
        }

        previousPlanType = data.user_type as UserType;
      }

      // Update the user's profile with the new plan type
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ user_type: newPlanType })
        .eq("id", userId);

      if (updateError) {
        console.error("Error updating user plan:", updateError);
        throw new Error("Failed to update subscription plan");
      }

      // Handle the special case of upgrading from starter to pro
      if (previousPlanType === "starter" && newPlanType === "pro") {
        // Get current report usage
        const { data: usageData, error: usageError } = await supabase
          .from("report_usage")
          .select("reports_generated")
          .eq("user_id", userId)
          .single();

        if (usageError && usageError.code !== "PGRST116") {
          console.error("Error fetching report usage for upgrade:", usageError);
          throw new Error("Failed to fetch report usage for upgrade");
        }

        if (usageData) {
          // Calculate remaining reports from starter plan
          const starterLimit = PLAN_LIMITS.starter;
          const usedReports = usageData.reports_generated;
          const remainingReports = Math.max(0, starterLimit - usedReports);

          // Reset report count to give them 30 new reports plus remaining reports
          // This effectively gives them 30 more reports plus whatever they had left
          const newReportCount = Math.max(
            0,
            PLAN_LIMITS.pro - remainingReports
          );

          const { error: resetError } = await supabase
            .from("report_usage")
            .update({ reports_generated: newReportCount })
            .eq("user_id", userId);

          if (resetError) {
            console.error(
              "Error resetting report count after upgrade:",
              resetError
            );
            throw new Error("Failed to reset report count after upgrade");
          }

          toast.success(
            `Upgraded to Pro! You now have ${PLAN_LIMITS.pro} reports per month, plus your ${remainingReports} remaining reports from your Starter plan.`
          );
        }
      } else {
        // For brand new subscriptions, reset report count to 0
        if (previousPlanType === "free") {
          const { error: resetError } = await supabase
            .from("report_usage")
            .update({ reports_generated: 0 })
            .eq("user_id", userId);

          if (resetError) {
            console.error("Error resetting report count:", resetError);
          }
        }

        toast.success(`Successfully upgraded to ${newPlanType} plan!`);
      }

      return true;
    } catch (error) {
      console.error("Error handling subscription upgrade:", error);
      toast.error("Failed to process subscription upgrade");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset report counts at the beginning of a new month
   * This would typically be called by a scheduled function
   */
  const resetMonthlyReportCounts = async () => {
    try {
      // Reset all report counts to zero
      const { error } = await supabase
        .from("report_usage")
        .update({ reports_generated: 0 })
        .neq("user_id", ""); // Update all records

      if (error) {
        console.error("Error resetting monthly report counts:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error resetting monthly report counts:", error);
      return false;
    }
  };

  /**
   * Refresh the user's subscription status
   */
  const refreshSubscriptionStatus = async (userId: string) => {
    setIsLoading(true);
    try {
      const subscription = await getUserSubscription(userId);
      return subscription;
    } catch (error) {
      console.error("Error refreshing subscription:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getUserSubscription,
    checkReportLimit,
    markFreeReportUsed,
    updateReportUsage,
    handleSubscriptionUpgrade,
    resetMonthlyReportCounts,
    refreshSubscriptionStatus,
    isLoading,
  };
};
